<template>
  <div class="flex flex-col h-full bg-white rounded-xl border border-gray-200 card-shadow overflow-hidden">

    <!-- New Selection -->
    <div v-if="markInTime !== null" class="p-4 border-b border-gray-100">
      <div class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
        {{ editingCutIndex !== null ? 'Editing Cut #' + (editingCutIndex + 1) : 'New Selection' }}
      </div>
      <div class="bg-gray-50 rounded-lg p-3 space-y-2">
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">IN</span>
          <div class="flex items-center gap-1.5">
            <button @click="$emit('nudge-in', -1)" class="w-7 h-7 rounded-md bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-colors" title="-1s">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span class="font-data text-xs font-medium text-gray-900 w-16 text-center">{{ formatTime(markInTime) }}</span>
            <button @click="$emit('nudge-in', 1)" class="w-7 h-7 rounded-md bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-colors" title="+1s">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <!-- Preview In -->
            <button @click="$emit('preview-point', 'in')" :class="['w-7 h-7 rounded-md flex items-center justify-center transition-colors', previewMode === 'in' ? 'bg-[var(--color-primary)] text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400']" title="Preview In (±2s)">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            </button>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">OUT</span>
          <div class="flex items-center gap-1.5">
            <button @click="$emit('nudge-out', -1)" :disabled="markOutTime === null" class="w-7 h-7 rounded-md bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-colors disabled:opacity-30" title="-1s">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <span class="font-data text-xs font-medium text-gray-900 w-16 text-center">{{ markOutTime !== null ? formatTime(markOutTime) : '--:--' }}</span>
            <button @click="$emit('nudge-out', 1)" :disabled="markOutTime === null" class="w-7 h-7 rounded-md bg-white border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:border-gray-400 transition-colors disabled:opacity-30" title="+1s">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <!-- Preview Out -->
            <button @click="$emit('preview-point', 'out')" :disabled="markOutTime === null" :class="['w-7 h-7 rounded-md flex items-center justify-center transition-colors disabled:opacity-30', previewMode === 'out' ? 'bg-[var(--color-primary)] text-white' : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400']" title="Preview Out (±2s)">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            </button>
          </div>
        </div>
        <div class="flex items-center justify-between pt-1">
          <span class="text-xs text-gray-500">Duration: <span class="text-gray-900 font-data">{{ hasSelection ? formatTimeMini(markOutTime - markInTime) : '--:--' }}</span></span>
          <!-- Loop buttons -->
          <div class="flex items-center gap-1">
            <button @click="$emit('loop-selection')" :disabled="!hasSelection" :class="['px-2 py-0.5 rounded text-xs font-medium border transition-colors disabled:opacity-30', loopMode === 'selection' ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300']" title="Loop selection">
              <svg class="w-3 h-3 inline -mt-0.5 mr-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
              Loop
            </button>
          </div>
        </div>
        <div class="flex gap-2 mt-1">
          <button v-if="editingCutIndex !== null" @click="$emit('cancel-edit')" class="flex-1 h-9 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200 transition-colors">Cancel</button>
          <button @click="$emit('add-cut')" :disabled="!hasSelection || playing" class="flex-1 h-9 rounded-lg bg-[var(--color-primary)] text-white text-xs font-medium hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-30">
            {{ editingCutIndex !== null ? 'Update Cut' : 'Add to Queue' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Cut Queue -->
    <div class="flex-1 overflow-y-auto">
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Cut Queue ({{ cuts.length }})</span>
        <button v-if="cuts.length" @click="$emit('clear-queue')" class="text-xs text-red-500 hover:text-red-600 transition-colors">Clear All</button>
      </div>

      <div v-if="cuts.length" class="p-2 space-y-1">
        <div v-for="(cut, i) in cuts" :key="cut.id"
          :class="['flex items-center gap-2 px-3 py-2 rounded-lg transition-colors group cursor-pointer',
            editingCutIndex === i ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 hover:bg-gray-100']"
          @click="$emit('edit-cut', i)"
        >
          <span class="w-5 h-5 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center shrink-0">{{ i + 1 }}</span>
          <div class="flex-1 min-w-0">
            <p class="font-data text-xs text-gray-600">{{ formatTime(cut.inTime) }} → {{ formatTime(cut.outTime) }}</p>
            <p class="text-xs text-gray-400">
              <svg class="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {{ formatTimeMini(cut.outTime - cut.inTime) }}
            </p>
          </div>
          <div class="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button @click.stop="$emit('go-to-cut', cut)" class="p-1 text-gray-400 hover:text-gray-900 transition-colors" title="Go to">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="10"/></svg>
            </button>
            <button @click.stop="$emit('remove-cut', i)" class="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Remove">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
      </div>

      <div v-else class="px-4 py-8 text-center">
        <svg class="w-8 h-8 text-gray-300 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/></svg>
        <p class="text-xs text-gray-500 font-medium">No cuts yet</p>
        <p class="text-xs text-gray-400 mt-0.5">Use Mark In/Out to select a range, then click "Add Cut"</p>
      </div>
    </div>

    <!-- Queue Summary -->
    <div v-if="cuts.length" class="px-4 py-2 border-t border-gray-100 bg-gray-50">
      <div class="flex items-center justify-between">
        <span class="text-xs text-gray-500">Total: <span class="text-gray-900 font-data font-medium">{{ formatTimeMini(totalCutsDuration) }}</span></span>
        <!-- Loop All -->
        <button @click="$emit('loop-all')" :disabled="cuts.length === 0" :class="['px-2 py-0.5 rounded text-xs font-medium border transition-colors disabled:opacity-30', loopMode === 'all' ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300']" title="Loop all cuts">
          <svg class="w-3 h-3 inline -mt-0.5 mr-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
          Loop All
        </button>
      </div>
    </div>

    <!-- Export Form -->
    <div v-if="cuts.length" class="p-4 border-t border-gray-100 space-y-3">
      <div>
        <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Title</label>
        <input :value="exportTitle" @input="$emit('update:exportTitle', $event.target.value)" type="text" placeholder="My Clip" class="w-full h-9 px-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-[var(--color-primary)] transition-colors" />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Description</label>
        <textarea :value="exportDescription" @input="$emit('update:exportDescription', $event.target.value)" rows="3" placeholder="Add a description (optional)" class="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-[var(--color-primary)] transition-colors resize-y" />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Tags</label>
        <TagSelect v-model="localTags" />
      </div>
      <div>
        <label class="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Category</label>
        <CategorySelect v-model="localCategoryIds" />
      </div>
      <button @click="$emit('show-formats')" :disabled="!exportTitle?.trim()" class="w-full h-10 rounded-lg bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-30">
        <svg class="w-4 h-4 inline -mt-0.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
        Choose Formats & Export
      </button>
    </div>

    <!-- Session Exports -->
    <div v-if="sessionExports.length" class="border-t border-gray-100">
      <div class="px-4 py-2">
        <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">Exports</span>
      </div>
      <div class="px-2 pb-2 space-y-1 max-h-[300px] overflow-y-auto">
        <div v-for="exp in sessionExports" :key="exp.media_id || exp.id" class="px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-100">
          <div class="flex items-center justify-between gap-2">
            <span class="text-xs font-medium text-gray-900 truncate">{{ exp.title }}</span>
            <div class="flex items-center gap-1.5 shrink-0">
              <span :class="['text-xs font-semibold px-2 py-0.5 rounded-full uppercase', statusClass(exp.status)]">{{ exp.status }}</span>
              <button @click="$emit('remove-export', exp)" class="p-0.5 text-gray-300 hover:text-red-500 transition-colors" title="Remove">
                <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          </div>
          <!-- Format tags -->
          <div v-if="exp.formats && exp.formats.length" class="flex flex-wrap gap-1 mt-1.5">
            <span v-for="f in exp.formats" :key="f" class="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">{{ f }}</span>
          </div>
          <!-- View media link when complete -->
          <router-link
            v-if="exp.media_id && ['complete', 'success'].includes(exp.status)"
            :to="{ name: 'medias-edit', params: { id: exp.media_id } }"
            class="inline-flex items-center gap-1 mt-1.5 text-xs font-medium text-[var(--color-primary)] hover:underline"
          >
            View media
            <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
          </router-link>
          <!-- Progress bar -->
          <div v-if="exp.progress != null && !['complete', 'success', 'failed'].includes(exp.status)" class="flex items-center gap-2 mt-2">
            <div class="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div class="h-full bg-[var(--color-primary)] rounded-full transition-all duration-300" :style="{ width: (exp.progress || 0) + '%' }" />
            </div>
            <span class="text-xs font-data text-gray-500 shrink-0">{{ exp.progress || 0 }}%</span>
          </div>
          <!-- Error message -->
          <div v-if="exp.status === 'failed'" class="flex items-center gap-1 mt-1.5 text-xs text-red-500">
            <svg class="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            {{ exp.error || 'Export failed' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import TagSelect from '../../../components/TagSelect.vue'
import CategorySelect from '../../../components/CategorySelect.vue'

export default {
  name: 'EditorCutPanel',
  components: { TagSelect, CategorySelect },
  props: {
    exportTitle: String,
    exportDescription: { type: String, default: '' },
    selectedTags: Array,
    selectedCategoryIds: { type: Array, default: () => [] }
  },
  emits: [
    'add-cut', 'remove-cut', 'clear-queue', 'go-to-cut', 'edit-cut', 'cancel-edit',
    'nudge-in', 'nudge-out', 'preview-point', 'loop-selection', 'loop-all',
    'show-formats', 'remove-export',
    'update:exportTitle', 'update:exportDescription', 'update:selectedTags', 'update:selectedCategoryIds'
  ],

  computed: {
    ...mapState('editor', ['markInTime', 'markOutTime', 'cuts', 'sessionExports', 'editingCutIndex', 'loopMode', 'previewMode', 'playing']),
    ...mapGetters('editor', ['hasSelection', 'totalCutsDuration']),
    localTags: {
      get() { return this.selectedTags },
      set(v) { this.$emit('update:selectedTags', v) }
    },
    localCategoryIds: {
      get() { return this.selectedCategoryIds },
      set(v) { this.$emit('update:selectedCategoryIds', v) }
    }
  },

  methods: {
    formatTime(s) {
      if (s == null || isNaN(s)) return '0:00'
      const startDate = this.$store.state.editor.startDate
      if (startDate) {
        const d = new Date(startDate.getTime() + s * 1000)
        const h = String(d.getHours()).padStart(2, '0')
        const m = String(d.getMinutes()).padStart(2, '0')
        const sec = String(d.getSeconds()).padStart(2, '0')
        return `${h}:${m}:${sec}`
      }
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const sec = Math.floor(s % 60)
      if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
      return `${m}:${String(sec).padStart(2, '0')}`
    },
    formatTimeMini(s) {
      if (!s || isNaN(s)) return '0:00'
      const m = Math.floor(s / 60)
      const sec = Math.floor(s % 60)
      return `${m}:${String(sec).padStart(2, '0')}`
    },
    statusClass(status) {
      switch (status) {
        case 'complete': case 'success': return 'bg-green-50 text-green-600'
        case 'failed': return 'bg-red-50 text-red-500'
        case 'processing': return 'bg-blue-50 text-blue-500'
        case 'uploading': return 'bg-emerald-50 text-emerald-500'
        case 'queued': case 'queue': return 'bg-amber-50 text-amber-500'
        default: return 'bg-gray-100 text-gray-500'
      }
    }
  }
}
</script>
